import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router";

import useDemographics from "/b2c/query/useDemographics.b2c";
import { List, Text } from "/component/base";
import * as Icons from "/component/base/Icon";
import InfoBanner from "/component/base/InfoBanner";
import MainContent from "/component/layout/MainContent";
import LoadingSkeleton from "/component/partial/LoadingSkeleton";
import MainContentLoginCta from "/component/partial/MainContentLoginCta";
import routes from "/constant/url.constant";
import { useAuth, useInfoBanners, useTranslation } from "/hook";
import { layout } from "/styles";
import { reactPlugin } from "/util/appInsights.util";
import { hasFeatureFlagDisabled } from "/util/featureFlags/featureFlags";

import { OptionProp } from "./AcccountIndex.types";
import {
  AccessoryWrapper,
  FullWidthAnchor,
  ListContainer,
  MyAccountItem,
} from "./AccountIndex.styles";

const AccountIndex = () => {
  const { data, loading: isDemographicsLoading } = useDemographics();
  const history = useHistory();
  const { enabled, showIcon, messageText, linkText, link, variant } = useInfoBanners("my_account");

  const { isAuthenticated, login } = useAuth();
  const { t } = useTranslation("account");

  //Not authorized show an empty state
  if (!isAuthenticated) {
    return (
      <MainContent pageTitle={t("title")}>
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>

        <MainContentLoginCta
          title={t("empty.title")}
          subtitle={t("empty.subtitle")}
          login={login}
        />
      </MainContent>
    );
  }

  const options: OptionProp[] = [
    {
      Icon: Icons.User,
      title: t("personalInfo"),
      to: routes.personalInformation,
    },
    {
      Icon: Icons.BillPayment,
      title: t("billPayments"),
      to: routes.billingAccounts,
    },
    {
      Icon: Icons.Certificate,
      title: t("insurance"),
      to: routes.insurances,
      hide: hasFeatureFlagDisabled("insurance_enabled"),
    },
    {
      Icon: Icons.NewSettings,
      title: t("accountSettings"),
      to: routes.settings,
    },
    {
      Icon: Icons.Caregiver,
      title: t("caregiver"),
      to: routes.caregiver,
    },
    {
      Icon: Icons.Smile,
      title: t("feedback"),
      to: routes.feedback,
      hide: !(process.env.FEATURE_ENABLE_FEEDBACK === "true"),
    },
    {
      Icon: Icons.Lock,
      title: t("tosPrivacy"),
      href: process.env.COMBINED_TOS_PRIVACY_URL,
      hide: !process.env.COMBINED_TOS_PRIVACY_URL,
    },
  ]
    .filter((o) => !o.hide)
    .filter((el) => el) as OptionProp[];

  return (
    <MainContent
      pageTitle={[
        data?.patient?.demographics?.firstName,
        data?.patient?.demographics?.lastName,
      ].join(" ")}
    >
      <Helmet>
        <title>{t("title")}</title>
      </Helmet>

      {/* HERE // 16px padding + 24px marginBottom */}
      <InfoBanner
        enabled={enabled}
        showIcon={showIcon}
        linkText={linkText}
        link={link}
        variant={variant}
        css={{ marginBottom: 24 }}
      >
        <Text variant="body1">{messageText}</Text>
      </InfoBanner>

      {isDemographicsLoading ? (
        <LoadingSkeleton skeletonVariant="list-item" />
      ) : (
        <ListContainer>
          <List
            as="Buttons"
            hideLastChildBorder
            onClick={({ to, href }) => {
              if (!href && to) {
                history.push(to);
              }
            }}
            data={options}
            renderItem={({ accessory, Icon, title, href }: OptionProp) => (
              <>
                {href ? (
                  <FullWidthAnchor href={href} rel="noreferrer" target="_blank">
                    <MyAccountItem>
                      <Icon
                        color="brandSecondary"
                        background={{ size: 54, color: "brandSecondaryLight" }}
                      />
                      <AccessoryWrapper>
                        <Text variant="body1Bold">{t("tosPrivacy")}</Text>
                      </AccessoryWrapper>
                    </MyAccountItem>
                  </FullWidthAnchor>
                ) : (
                  <MyAccountItem>
                    <Icon
                      color="brandSecondary"
                      background={{ size: 54, color: "brandSecondaryLight" }}
                    />
                    <AccessoryWrapper>
                      <Text variant="body1Bold">{title}</Text>
                      {accessory}
                    </AccessoryWrapper>
                  </MyAccountItem>
                )}
              </>
            )}
          />
        </ListContainer>
      )}
      {!!process.env.GITHUB_COMMIT_HASH && (
        <Text css={layout.margin("standard", "skip", "skip")} element="div">
          VERSION: {process.env.GITHUB_COMMIT_HASH}
        </Text>
      )}
    </MainContent>
  );
};

export default withAITracking(reactPlugin, AccountIndex, undefined, "ai-tracking");
